// src/components/Footer.js
import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { Mail, Facebook, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import FooterAd from "./ads/FooterAd";

const Footer = () => {
  const { language } = useContext(AppContext);
  const [offset, setOffset] = useState("2rem");
  const [fade, setFade] = useState(false);

  const content = {
    el: {
      contact: "Επικοινωνία",
      email: "vrestoedo@mail.com",
      facebook: "Facebook Page",
      creditTop: (
        <>
          Κατασκευή & επιμέλεια ιστοσελίδας:{" "}
          <span className="whitespace-nowrap">Ρουσσουνέλος Μιχάλης</span>
        </>
      ),
      creditBottom: (
        <>
          © 2025-2026 VrestoEdo.gr | ver.2.0 (260531){" "}
          <span className="whitespace-nowrap">
            Όλα τα δικαιώματα διατηρούνται.
          </span>
        </>
      ),
      top: "Πάνω",
      subtitle:
        "Η πύλη σας για γρήγορη πρόσβαση σε online έγγραφα και υπηρεσίες.",
      mailTooltip: "Πατήστε για αποστολή mail στην Ομάδα Υποστήριξης",
      fbTooltip: "Μετάβαση στη σελίδα μας στο Facebook",
      termsLink: "Όροι Χρήσης & Πολιτική Απορρήτου",
    },
    en: {
      contact: "Contact",
      email: "vrestoedo@mail.com",
      facebook: "Facebook Page",
      creditTop: (
        <>
          Website design & maintenance:{" "}
          <span className="whitespace-nowrap">Roussounelos Michalis</span>
        </>
      ),
      creditBottom: (
        <>
          © 2025-2026 VrestoEdo.gr | ver.2.0 (260531){" "}
          <span className="whitespace-nowrap">All rights reserved.</span>
        </>
      ),
      top: "Top",
      subtitle:
        "Your gateway for quick access to online documents and services.",
      mailTooltip: "Click to send an email to our Support Team",
      fbTooltip: "Visit our Facebook page",
      termsLink: "Terms of Use & Privacy Policy",
    },
  };

  const lang = content[language];

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollBottom = window.innerHeight + scrollY;
      const docHeight = document.body.offsetHeight;

      const show = scrollY > 100;
      setFade(show);

      const nearFooter = docHeight - scrollBottom < 240;
      setOffset(nearFooter ? "12rem" : "2rem");
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <footer className="footer-glass w-full mt-20 pt-10 pb-6 px-6 sm:px-10 text-sm relative z-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-blue-200 text-center md:text-left items-start">
          {/* ----- Column 1 ----- */}
          <div className="flex flex-col items-center md:items-start w-full">
            <h3 className="font-semibold text-lg text-white mb-2">
              VrestoEdo.gr
            </h3>
            <p className="text-blue-200 leading-relaxed text-center md:text-left max-w-[340px]">
              {lang.subtitle}
            </p>
            <div className="mt-3 text-center md:text-left space-y-1">
              <div>
                <Link to="/terms" className="underline hover:text-blue-300">
                  {lang.termsLink}
                </Link>
              </div>
              <div>
                <Link to="/about" className="underline hover:text-blue-300">
                  {language === "el" ? "Σχετικά με εμάς" : "About us"}
                </Link>
              </div>
              <div>
                <Link to="/article/1" className="underline hover:text-blue-300">
                  {language === "el"
                    ? "Οδηγός: Πώς να βρω έγγραφα στο gov.gr"
                    : "Guide: How to find documents on gov.gr"}
                </Link>
              </div>
            </div>
          </div>

          {/* ----- Column 2 ----- */}
          <div className="flex flex-col items-center justify-center text-blue-300 text-xs font-medium leading-relaxed text-center max-w-[340px] mx-auto">
            <p>{lang.creditTop}</p>
            <p className="mt-1">{lang.creditBottom}</p>
          </div>

          {/* ----- Column 3 ----- */}
          <div className="flex flex-col items-center md:items-end w-full relative">
            <p className="font-medium text-white mb-2">{lang.contact}</p>

            {/* EMAIL */}
            <div className="relative inline-flex items-center group py-1">
              <a
                href="mailto:vrestoedo@mail.com"
                className="hover:text-blue-400 inline-flex items-center gap-1 transition-colors"
                aria-describedby="mail-tip"
              >
                <Mail size={16} /> {lang.email}
              </a>
              <span
                id="mail-tip"
                className="
                  pointer-events-none select-none
                  absolute -top-6 right-0
                  bg-black/70 text-xs text-white px-2 py-1 rounded-md whitespace-nowrap
                  opacity-0 translate-y-1
                  transition-all duration-300
                  group-hover:opacity-100 group-hover:translate-y-0
                "
                role="tooltip"
              >
                {lang.mailTooltip}
              </span>
            </div>

            {/* FACEBOOK */}
            <div className="relative inline-flex items-center group py-1 mt-2">
              <a
                href="https://www.facebook.com/profile.php?id=61573088055041"
                target="_blank"
                rel="noreferrer"
                className="hover:text-blue-400 inline-flex items-center gap-1 transition-colors"
                aria-describedby="fb-tip"
              >
                <Facebook size={16} /> {lang.facebook}
              </a>
              <span
                id="fb-tip"
                className="
                  pointer-events-none select-none
                  absolute -top-6 right-0
                  bg-black/70 text-xs text-white px-2 py-1 rounded-md whitespace-nowrap
                  opacity-0 translate-y-1
                  transition-all duration-300
                  group-hover:opacity-100 group-hover:translate-y-0
                "
                role="tooltip"
              >
                {lang.fbTooltip}
              </span>
            </div>
          </div>
        </div>

        {/* ✅ Διαφήμιση footer */}
        <FooterAd />
      </footer>

      {/* Scroll To Top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`
          fixed right-6 md:right-8 
          bg-white/10 text-white p-2 rounded-lg border border-white/20 shadow-lg
          hover:bg-white/20 transition-all duration-500 ease-in-out
          backdrop-blur-md cursor-pointer isolate
          z-[999999] pointer-events-auto
          ${fade ? "opacity-100" : "opacity-0 pointer-events-none"}
        `}
        style={{
          bottom: offset,
          transition: "opacity 0.5s ease, bottom 0.3s ease",
        }}
        aria-label={lang.top}
      >
        <ArrowUp size={20} />
      </button>
    </>
  );
};

export default Footer;